<template>
  <b-modal
    id="chat-agent-step-modal"
    ref="chatAgentStepModal"
    :title="$t('title.editStep')"
    size="lg"
    hide-footer
    content-class="shadow"
    @hide="onModalHide"
  >
    <validation-observer ref="stepForm" v-slot="{ invalid }">
      <b-form @submit.prevent="validateAndSubmit">
        <b-form-group :label="$t('field.stepDescription')" class="mt-2">
          <b-form-input
            v-model="step.name"
            :placeholder="$t('placeholder.stepName')"
            required
          />
        </b-form-group>

        <b-form-group :label="$t('field.stepDescription')" class="mt-2">
          <b-form-textarea
            v-model="step.description"
            :placeholder="$t('placeholder.stepDescription')"
            required
          />
        </b-form-group>

        <b-form-group :label="$t('field.parameters')" class="mt-2">
          <b-row
            v-for="(param, paramIndex) in step.parameters"
            :key="paramIndex"
            class="mb-2"
          >
            <b-col cols="5">
              <b-form-input
                v-model="param.name"
                :placeholder="$t('placeholder.paramName')"
                required
              />
            </b-col>
            <b-col cols="5">
              <b-form-input
                v-model="param.key"
                :placeholder="$t('placeholder.paramKey')"
                required
              />
            </b-col>
            <b-col cols="2" class="d-flex align-items-center">
              <b-button
                v-if="step.parameters.length > 1"
                variant="outline-danger"
                size="sm"
                @click="removeParameter(paramIndex)"
                class="ml-2"
              >
                {{ $t('button.remove') }}
              </b-button>
            </b-col>
          </b-row>
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="addParameter"
          >
            {{ $t('button.addParameter') }}
          </b-button>
        </b-form-group>

        <b-form-group :label="$t('field.callbackUrl')" class="mt-2">
          <b-form-input
            v-model="step.callbackUrl"
            :placeholder="$t('placeholder.callbackUrl')"
            type="url"
          />
        </b-form-group>

        <b-row class="mt-3">
          <b-col class="text-right">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="closeModal"
            >
              {{ $t('button.cancel') }}
            </b-button>
            <b-button type="submit" variant="primary" :disabled="invalid || loading">
              <span v-if="loading" class="spinner-border spinner-border-sm mr-1"></span>
              {{ $t('button.update') }}
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </validation-observer>
  </b-modal>
</template>

<script>
import {
  BRow,
  BCol,
  BModal,
  BButton,
  BForm,
  BFormInput,
  BFormGroup,
  BFormTextarea
} from 'bootstrap-vue'
import Repository from '@/repositories/RepositoryFactory'

const ChatAgentRegistry = Repository.get('chatAgent')

export default {
  name: 'ChatAgentStepModal',

  components: {
    BRow,
    BCol,
    BModal,
    BButton,
    BForm,
    BFormInput,
    BFormGroup,
    BFormTextarea
  },

  props: {
    show: {
      type: Boolean,
      required: true
    },
    agentData: {
      type: Object,
      required: true
    },
    stepIndex: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      step: {
        name: '',
        description: '',
        parameters: [],
        callbackUrl: ''
      },
      loading: false
    }
  },

  watch: {
    show(val) {
      if (val) {
        this.showModal()
        this.initializeStepData()
      }
    },
    'agentData.steps': {
      handler(val) {
        if (val && this.show) {
          this.initializeStepData()
        }
      },
      deep: true
    }
  },

  methods: {
    initializeStepData() {
      const stepData = this.agentData.steps[this.stepIndex]
      this.step = {
        ...stepData,
        parameters: stepData.parameters.map(param => ({ ...param }))
      }
    },

    createDefaultParameter() {
      return {
        name: '',
        key: ''
      }
    },

    addParameter() {
      this.step.parameters.push(this.createDefaultParameter())
    },

    removeParameter(paramIndex) {
      if (this.step.parameters.length > 1) {
        this.step.parameters.splice(paramIndex, 1)
      } else {
        this.$bvToast.toast(this.$t('message.atLeastOneParameter'), {
          variant: 'warning'
        })
      }
    },

    validateAndSubmit() {
      this.$refs.stepForm.validate().then(success => {
        if (success) {
          this.updateStep()
        }
      })
    },

    async updateStep() {
      this.loading = true
      try {
        const updatedAgent = {
          ...this.agentData,
          steps: [...this.agentData.steps]
        }

        // Update the specific step while maintaining its index
        updatedAgent.steps[this.stepIndex] = { ...this.step }

        const response = await ChatAgentRegistry.update(updatedAgent.id, updatedAgent)
        const { data } = response.data
        if (data) {
          this.$emit('update', data)
          this.closeModal()
          this.$bvToast.toast(this.$t('message.updateStepSuccess'), {
            variant: 'success'
          })
        }
      } catch (error) {
        console.error('Error updating step:', error)
        this.$bvToast.toast(this.$t('message.updateStepError'), {
          variant: 'danger'
        })
      } finally {
        this.loading = false
      }
    },

    closeModal() {
      this.$refs.chatAgentStepModal.hide()
      this.$emit('hide-modal')
    },

    showModal() {
      this.$refs.chatAgentStepModal.show()
    },

    onModalHide() {
      this.$emit('hide-modal')
    }
  }
}
</script>

<style lang="scss" scoped>
.border {
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;
  background-color: #f8f9fa;
}
</style>